
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ProfileAvatar } from "./ProfileAvatar";
import { CVUpload } from "./CVUpload";
import { Save, User, Briefcase, FileText, Building } from "lucide-react";

interface ProfileEditProps {
  initialData: any;
  avatarUrl: string | undefined;
  setAvatarUrl: (url: string) => void;
  cvUrl: string | undefined;
  setCvUrl: (url: string) => void;
  onSubmit: (data: any) => Promise<void>;
  showCompanyTab?: boolean;
}

export const ProfileEdit = ({ 
  initialData, 
  avatarUrl, 
  setAvatarUrl,
  cvUrl,
  setCvUrl,
  onSubmit,
  showCompanyTab = true
}: ProfileEditProps) => {
  const form = useForm({
    defaultValues: {
      full_name: initialData?.full_name || "",
      location: initialData?.location || "",
      phone_number: initialData?.phone_number || "",
      job_title: initialData?.job_title || "",
      organization: initialData?.organization || "",
      company_size: initialData?.company_size || "",
      company_description: initialData?.company_description || "",
      company_industry: initialData?.company_industry || "",
      calendly_url: initialData?.calendly_url || "",
    },
  });

  const handleSubmit = async (data: any) => {
    await onSubmit(data);
  };

  return (
    <div className="space-y-8 p-6">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-8">
          {/* Profile Picture Card */}
          <Card className="border border-border shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg font-semibold text-foreground">
                <User className="h-5 w-5 text-primary" />
                Profile Picture
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ProfileAvatar
                avatarUrl={avatarUrl}
                setAvatarUrl={setAvatarUrl}
                userInitial={initialData?.full_name?.[0] || "U"}
              />
            </CardContent>
          </Card>

          {/* Personal Information Card */}
          <Card className="border border-border shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg font-semibold text-foreground">
                <User className="h-5 w-5 text-primary" />
                Personal Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <FormField
                control={form.control}
                name="full_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium text-foreground">Full Name</FormLabel>
                    <FormControl>
                      <Input 
                        {...field} 
                        placeholder="Enter your full name" 
                        className="h-11 border border-input bg-background transition-colors focus:border-ring"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="location"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium text-foreground">Location</FormLabel>
                    <FormControl>
                      <Input 
                        {...field} 
                        placeholder="City, Country" 
                        className="h-11 border border-input bg-background transition-colors focus:border-ring"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="phone_number"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium text-foreground">Phone Number</FormLabel>
                    <FormControl>
                      <Input 
                        {...field} 
                        placeholder="+1 (555) 123-4567" 
                        className="h-11 border border-input bg-background transition-colors focus:border-ring"
                        type="tel"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Professional Information Card */}
          <Card className="border border-border shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg font-semibold text-foreground">
                <Briefcase className="h-5 w-5 text-primary" />
                Professional Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <FormField
                control={form.control}
                name="job_title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium text-foreground">Job Title</FormLabel>
                    <FormControl>
                      <Input 
                        {...field} 
                        placeholder="Your job title" 
                        className="h-11 border border-input bg-background transition-colors focus:border-ring"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="organization"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium text-foreground">Organization</FormLabel>
                    <FormControl>
                      <Input 
                        {...field} 
                        placeholder="Your organization" 
                        className="h-11 border border-input bg-background transition-colors focus:border-ring"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="calendly_url"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium text-foreground">Calendly URL (Optional)</FormLabel>
                    <FormControl>
                      <Input 
                        {...field} 
                        placeholder="https://calendly.com/your-username/meeting" 
                        className="h-11 border border-input bg-background transition-colors focus:border-ring"
                        type="url"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* CV/Resume Card */}
          <Card className="border border-border shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg font-semibold text-foreground">
                <FileText className="h-5 w-5 text-primary" />
                CV/Resume
              </CardTitle>
            </CardHeader>
            <CardContent>
              <CVUpload
                cvUrl={cvUrl}
                setCvUrl={setCvUrl}
              />
            </CardContent>
          </Card>

          {/* Company Information Card */}
          {showCompanyTab && (
            <Card className="border border-border shadow-sm">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-lg font-semibold text-foreground">
                  <Building className="h-5 w-5 text-primary" />
                  Company Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <FormField
                  control={form.control}
                  name="company_size"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-foreground">Company Size</FormLabel>
                      <FormControl>
                        <Input 
                          {...field} 
                          placeholder="e.g. 50-100 employees" 
                          className="h-11 border border-input bg-background transition-colors focus:border-ring"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="company_industry"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-foreground">Industry</FormLabel>
                      <FormControl>
                        <Input 
                          {...field} 
                          placeholder="e.g. Technology, Healthcare" 
                          className="h-11 border border-input bg-background transition-colors focus:border-ring"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="company_description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-foreground">Company Description</FormLabel>
                      <FormControl>
                        <Textarea 
                          {...field} 
                          placeholder="Tell us about your company" 
                          className="min-h-[120px] border border-input bg-background resize-none transition-colors focus:border-ring"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>
          )}

          {/* Save Button */}
          <div className="sticky bottom-6 flex justify-center pt-6">
            <Button 
              type="submit" 
              size="lg"
              className="gap-2 px-8 py-3 h-12 bg-secondary hover:bg-secondary-hover text-secondary-foreground font-medium rounded-lg shadow-md hover:shadow-lg transition-all duration-200"
              disabled={form.formState.isSubmitting || !form.formState.isDirty}
            >
              <Save className="h-4 w-4" />
              {form.formState.isSubmitting ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};
